import type { ComponentProps } from 'react';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import appConfig from 'app-config';
import type { DTOFollowInfo } from 'src/apis/dto/DTOFollowInfo';
import { useDirectMessageNavigation } from 'src/components/DirectMessage/context';
import { defaultProfileImage } from 'src/components/ProfileImage';
import ProfileModal from 'src/components/ProfileModal/Full';
import ProfileButtons from 'src/components/ProfileModal/Full/ProfileButtons';
import useCssMediaDevice from 'src/hooks/useCssMediaDevice';
import { useOpenDirectMessageThreadMutation } from 'src/hooks/useDirectMessageQuery';
import { showModalAtom } from 'src/stores/modal/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalType } from 'src/types/Modal';
import { formatDateDistance } from 'src/utils/date';
import { pushEvent } from 'src/utils/event-gateway';
import { ProfileImage, ProfileImageContainer, ActionButton, Container, NameWrap, OnlineDot, ProfileInfo, Name, Time } from './styles';
interface Props extends Pick<ComponentProps<'li'>, 'className'> {
  followInfo: DTOFollowInfo;
}
const FollowUserCard = ({
  followInfo,
  ...props
}: Props) => {
  const {
    t
  } = useTranslation();
  const {
    followId,
    isOnline,
    nickname,
    profileMediaInfo,
    messageThreadId
  } = followInfo;
  const lastOnlineAt = followInfo.lastOnlineAt == null ? null : new Date(followInfo.lastOnlineAt * 1000);
  const device = useCssMediaDevice();
  const showModal = useSetAtom(showModalAtom);
  const {
    openRoom
  } = useDirectMessageNavigation();
  const {
    mutate,
    isPending
  } = useOpenDirectMessageThreadMutation({
    idType: 'FOLLOW',
    path: 'MESSAGE_FOLLOWING_MESSAGE'
  }, {
    onSuccess: openRoom
  });
  const handleClickProfile = () => {
    if (!appConfig.features.userProfile.enabled) return;
    showModal({
      key: ModalType.PROFILE_MODAL,
      component: () => <ProfileModal type='FRIEND' id={followId} eventPage='main' eventSection='following_list' buttons={<ProfileButtons.FavoriteChat landFrom='MESSAGE_FOLLOWING' followInfo={followInfo} />} />
    });
  };
  const handleClickChatButton = () => {
    pushEvent({
      eventType: EVENT_TYPE.LOG,
      eventName: EVENT_NAME.ANYWHERE__CLICK_MESSAGE,
      eventParams: {
        action_category: 'click',
        tab: 'message',
        page: 'message',
        target: 'message',
        card_id: followId,
        section: 'following_list'
      }
    });
    if (messageThreadId) {
      openRoom(messageThreadId);
      return;
    }
    mutate(followId);
  };
  return <Container {...props} data-sentry-element="Container" data-sentry-component="FollowUserCard" data-sentry-source-file="Item.tsx">
      <ProfileInfo data-sentry-element="ProfileInfo" data-sentry-source-file="Item.tsx">
        <ProfileImageContainer onClick={handleClickProfile} data-sentry-element="ProfileImageContainer" data-sentry-source-file="Item.tsx">
          <ProfileImage src={profileMediaInfo?.thumbnailImageUrl ?? defaultProfileImage} alt={nickname} draggable={false} data-sentry-element="ProfileImage" data-sentry-source-file="Item.tsx" />
          {isOnline && <OnlineDot />}
        </ProfileImageContainer>
        <NameWrap data-sentry-element="NameWrap" data-sentry-source-file="Item.tsx">
          <Name data-sentry-element="Name" data-sentry-source-file="Item.tsx">{nickname}</Name>
          {/**
           * lastOnlineAt 은 초 단위 타임스탬프
           * @see {https://www.notion.so/hpcnt/Follow-Pin-API-Spec-caaefbee6dd947779a3ecdc5dab6bf8d?source=copy_link#05ff7aae59f345ee957a981807ea2ccc}
           */}
          {lastOnlineAt ? <Time dateTime={lastOnlineAt.toISOString()}>{formatDateDistance(lastOnlineAt)}</Time> : (/* TODO: lastOnlineAt 이 없을 때 정책을 어떻게 할 지 논의 */
        <Time as='span'>&nbsp;</Time>)}
        </NameWrap>
      </ProfileInfo>
      <ActionButton type='button' size={device === 'desktop' ? 'medium' : 'small'} variant='tertiary' onClick={handleClickChatButton} disabled={isPending} data-sentry-element="ActionButton" data-sentry-source-file="Item.tsx">
        {t('Chat')}
      </ActionButton>
    </Container>;
};
export default FollowUserCard;